import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useContext,
} from "react";
import styles from "./Livebids.module.scss";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Tooltip,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import { ButtonLoader } from "../../../elements/CustomLoader/Loader";
import _sendAPIRequest from "../../../helpers/api";
import { PortalApiUrls } from "../../../helpers/api-urls/PortalApiUrls";
import ScreenLoader from "../../../elements/CustomScreeenLoader/ScreenLoader";
import NoliveBidImg from "../../../assets/images/portal/bids/no_live_bid.png";
import { truncateString } from "../../../helpers/formatter";
import DOMPurify from "../../../utils/DomPurifier";
import { AlertContext } from "../../../contexts/AlertProvider";
// CountdownTimer Component
const CountdownTimer = ({ endDate, onExpire, label }) => {
  const expiredRef = useRef(false);

  const calculateRemainingTime = useCallback(() => {
    const now = new Date();
    const targetDate = new Date(endDate);
    const timeDiff = targetDate - now;

    if (timeDiff <= 0) {
      if (!expiredRef.current) {
        expiredRef.current = true;
        onExpire?.(true);
      }

      return { expired: true };
    }

    const hours = Math.floor(timeDiff / 1000 / 60 / 60);
    const minutes = Math.floor((timeDiff / 1000 / 60) % 60);
    const seconds = Math.floor((timeDiff / 1000) % 60);

    return { hours, minutes, seconds, expired: false };
  }, [endDate, onExpire]);

  const [timeLeft, setTimeLeft] = useState(calculateRemainingTime);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(calculateRemainingTime);
    }, 1000);

    return () => clearInterval(interval);
  }, [calculateRemainingTime]);

  const { hours, minutes, seconds, expired } = timeLeft;

  if (expired) {
    return (
      <Box className={styles["live-transition"]}>
        <ButtonLoader size={35} />
      </Box>
    );
  }

  return (
    <Grid
      container
      spacing={1}
      sx={{
        justifyContent: "center",
        mb: 2
      }}>
      {[
        { label: "Hrs", value: hours },
        { label: "Min", value: minutes },
        { label: "Sec", value: seconds },
      ].map((time, index) => (
        <Box key={index} className={styles["countDown_timer"]}>
          <Typography className={styles["countDown_value"]}>
            {time.value}
          </Typography>
          <Typography className={styles["countDown_label"]}>
            {time.label}
          </Typography>
        </Box>
      ))}
    </Grid>
  );
};

const Livebids = ({ listType }) => {
  const [screenLoader, setScreenLoader] = useState(true);
  const [btnLoader, setBtnLoader] = useState(false);
  const [liveBids, setLiveBids] = useState([]);
  const [upcomingBids, serUpcomingBids] = useState([]);
  const navigate = useNavigate();
  const previousUpcomingIds = useRef([]);
  const [expiredBids, setExpiredBids] = useState({});
  const { setAlert } = useContext(AlertContext);

  const retrieveLiveBids = useCallback(async () => {
    try {
      const response = await _sendAPIRequest(
        "GET",
        listType === "Created"
          ? PortalApiUrls.LIVE_BID_LIST_FOR_BUYER
          : PortalApiUrls.LIVE_BID_LIST_FOR_SUPPLIER,
        "",
        true,
      );

      if (response.status === 200) {
        const liveBids = response?.data?.live_bids || [];
        const upComingBids = response?.data?.upcoming_bids || [];
        const previousUpcoming = previousUpcomingIds.current;
        const newlyLiveBids = liveBids.filter((liveBid) =>
          previousUpcoming.includes(liveBid.id),
        );

        if (newlyLiveBids.length > 0) {
          setAlert({
            isVisible: true,
            message: "An upcoming bid is now LIVE!",
            severity: "success",
          });
        }

        setLiveBids(liveBids);
        serUpcomingBids(upComingBids);

        setExpiredBids((prev) => {
          const upcomingIds = upComingBids.map((b) => b.id);

          const cleanedState = {};

          upcomingIds.forEach((id) => {
            if (prev[id]) {
              cleanedState[id] = prev[id];
            }
          });

          return cleanedState;
        });

        previousUpcomingIds.current = upComingBids.map((bid) => bid.id);
      }
    } catch (error) {
      console.error("Error fetching live bids:", error);
    } finally {
      setScreenLoader(false);
    }
  }, [listType]);

  useEffect(() => {
    let interval;

    if (upcomingBids.length > 0 || liveBids.length > 0) {
      interval = setInterval(() => {
        retrieveLiveBids();
      }, 15000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [upcomingBids, retrieveLiveBids]);

  useEffect(() => {
    retrieveLiveBids();
  }, []);

  if (screenLoader) {
    return <ScreenLoader />;
  }

  return (
    <>
      {liveBids?.length > 0 || upcomingBids.length > 0 ? (
        <Box sx={{ p: 2, bgcolor: "#f9fafc" }}>
          {/* Live Bids Section Divider */}
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            Live Bids
          </Typography>
          <Box sx={{ mb: 3, borderBottom: "2px solid #ccc" }} />

          {/* Render Live Bids */}
          {liveBids?.length > 0 ? (
            liveBids.map((bid) => {
              const isClosed = new Date(bid?.bid_close_date) <= new Date();

              return (
                <Card
                  key={bid.id}
                  sx={{
                    mb: 3,
                    borderRadius: 3,
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                    overflow: "hidden",
                    transition: "transform 0.3s",

                    ":hover": {
                      transform: isClosed ? "none" : "scale(1.02)",
                    },

                    cursor: isClosed ? "default" : "pointer",
                    opacity: isClosed ? 0.8 : 1,
                  }}
                  onClick={() => {
                    if (isClosed) return;

                    listType === "Invited"
                      ? navigate(`/portal/liveBids/details/?id=${bid.id}`, {
                          state: { bid, listType },
                        })
                      : navigate(
                          `/portal/liveBids/details/?type=created&id=${bid.id}`,
                          { state: { bid, listType } },
                        );
                  }}
                >
                  <CardContent>
                    <Grid container spacing={2}>
                      {/* Bid Information */}
                      <Grid
                        size={{
                          xs: 12,
                          md: 8
                        }}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ mb: 1 }}
                        >
                          Bid ID: <strong>{bid?.formatted_number}</strong>
                        </Typography>

                        <Tooltip title={bid?.title}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: "bold",
                              color: "#333",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                            }}
                          >
                            {truncateString(bid?.title, 40)}
                          </Typography>
                        </Tooltip>

                        <Typography
                          variant="subtitle1"
                          sx={{ color: "#666", mb: 1, fontWeight: "medium" }}
                        >
                          By <strong>{bid?.company?.name}</strong>
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ color: "#555", lineHeight: 1.6 }}
                        >
                          <div
                            className={styles["col-data"]}
                            style={{ whiteSpace: "pre-line" }}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(bid?.description),
                            }}
                          />
                        </Typography>
                      </Grid>

                      {/* Countdown Timer and Button */}
                      <Grid
                        container
                        direction="column"
                        size={{
                          xs: 12,
                          md: 4
                        }}
                        sx={{
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center"
                        }}>
                        {!isClosed ? (
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: "bold",
                              color: "#333",
                              mb: 1,
                              textTransform: "uppercase",
                            }}
                          >
                            Bid Ends In:
                          </Typography>
                        ) : (
                          <Typography
                            variant="subtitle1"
                            className={styles["closing-blink"]}
                            sx={{
                              fontWeight: "bold",
                              color: "#333",
                              mb: 1,
                              textTransform: "uppercase",
                            }}
                          >
                            Closing Bid:
                          </Typography>
                        )}

                        <CountdownTimer
                          endDate={bid?.bid_close_date}
                          onExpire={() =>
                            setExpiredBids((prev) => ({
                              ...prev,
                              [bid.id]: true,
                            }))
                          }
                        />

                        {btnLoader ? (
                          <ButtonLoader size={60} />
                        ) : isClosed ? null : (
                          <Button
                            variant="contained"
                            className={styles["place-bid-btn"]}
                            endIcon={<GavelIcon />}
                            onClick={(e) => {
                              e.stopPropagation();

                              listType === "Invited"
                                ? navigate(
                                    `/portal/liveBids/details/?id=${bid.id}`,
                                    {
                                      state: { bid, listType },
                                    },
                                  )
                                : navigate(
                                    `/portal/liveBids/details/?type=created&id=${bid.id}`,
                                    { state: { bid, listType } },
                                  );
                            }}
                          >
                            {listType === "Invited"
                              ? "Place a Bid"
                              : "View Bid"}
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            // IF NO BID IS LIVE
            (<>
              <Box
                component="img"
                src={NoliveBidImg}
                alt="No Bid Available"
                className={styles["no-bid-img"]}
              />
              <Typography
                variant="h5"
                color="textSecondary"
                sx={{
                  fontWeight: "bolder"
                }}
              >
                No Live Bid Available
              </Typography>
            </>)
          )}

          {/* Upcoming Bids Section Divider */}
          <Typography variant="h6" sx={{ fontWeight: "bold", mt: 4, mb: 1 }}>
            Upcoming Bids
          </Typography>
          <Box sx={{ mb: 3, borderBottom: "2px solid #ccc" }} />

          {upcomingBids.length > 0 ? (
            upcomingBids.map((bid) => (
              <Tooltip title="Upcoming Bid - This bid yet to be live" arrow>
                <Card
                  key={bid.id}
                  sx={{
                    mb: 3,
                    borderRadius: 3,
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                    overflow: "hidden",
                    opacity: 0.6,
                    cursor: "wait",
                    pointerEvents: "auto",
                  }}
                  onClick={(e) => e.preventDefault()}
                >
                  <CardContent>
                    <Grid container spacing={2}>
                      {/* Bid Information */}
                      <Grid
                        size={{
                          xs: 12,
                          md: 8
                        }}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ mb: 1 }}
                        >
                          Bid ID: <strong>{bid?.formatted_number}</strong>
                        </Typography>
                        <Tooltip title={bid?.title}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: "bold",
                              color: "#333",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                            }}
                          >
                            {truncateString(bid?.title, 40)}
                          </Typography>
                        </Tooltip>
                        <Typography
                          variant="subtitle1"
                          sx={{ color: "#666", mb: 1, fontWeight: "medium" }}
                        >
                          By <strong>{bid?.company?.name}</strong>
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#555", lineHeight: 1.6 }}
                        >
                          <div
                            className={styles["col-data"]}
                            style={{ whiteSpace: "pre-line" }}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(bid?.description),
                            }}
                          ></div>
                        </Typography>
                      </Grid>

                      {/* Countdown Timer and Button */}
                      <Grid
                        container
                        direction="column"
                        size={{
                          xs: 12,
                          md: 4
                        }}
                        sx={{
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center"
                        }}>
                        {new Date(bid?.bid_open_date) > new Date() ? (
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: "bold",
                              color: "#333",
                              mb: 1,
                              textTransform: "uppercase",
                            }}
                          >
                            Bid Starts In:
                          </Typography>
                        ) : (
                          <Typography
                            variant="subtitle1"
                            className={
                              expiredBids[bid.id]
                                ? styles["activating-blink"]
                                : ""
                            }
                            sx={{
                              fontWeight: "bold",
                              color: "#333",
                              mb: 1,
                              textTransform: "uppercase",
                            }}
                          >
                            Activating Bid:
                          </Typography>
                        )}

                        <CountdownTimer
                          endDate={bid?.bid_open_date}
                          label="Going Live..."
                          onExpire={() =>
                            setExpiredBids((prev) => ({
                              ...prev,
                              [bid.id]: true,
                            }))
                          }
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Tooltip>
            ))
          ) : (
            // IF NO BID IS COMES UNDER COMING BID
            (<Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                bgcolor: "#f0f4f8",
                p: 2,
                backgroundColor: "none",
              }}
            >
              <Box
                component="img"
                src={NoliveBidImg}
                alt="No Bid Available"
                className={styles["no-bid-img"]}
              />
              <Typography
                variant="h5"
                color="textSecondary"
                sx={{
                  fontWeight: "bolder"
                }}
              >
                No Upcoming bid for you
              </Typography>
            </Box>)
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            bgcolor: "#f0f4f8",
            p: 2,
            backgroundColor: "none",
          }}
        >
          <Box
            component="img"
            src={NoliveBidImg}
            alt="No Bid Available"
            className={styles["no-bid-img"]}
          />
          <Typography variant="h5" color="textSecondary" sx={{
            fontWeight: "bolder"
          }}>
            No Bid Available
          </Typography>
        </Box>
      )}
    </>
  );
};

export default Livebids;
